import React, { useCallback, useEffect, useState } from 'react'
import Swal from 'sweetalert2'
import styles from '../AdminDashboard.module.scss'
import { config } from '../../../config'

type WebhookInfo = {
  enabled: boolean
  masked_url: string
}

type PublishRun = {
  id: string
  run_id?: string
  date_yyyymmdd?: string
  updated_at?: string
  enqueue_started_at?: string
  enqueue_complete_at?: string

  expected_batches?: number
  expected_entities?: number
  processed_batches?: number
  processed_entities?: number
  processed_published?: number
  processed_failed?: number

  end_webhook_sent_at?: string
  end_webhook_sent_by?: string
  end_webhook_last_error_at?: string
  end_webhook_failed_at?: string
  end_webhook_fail_reason?: string
}

type PublishRunsResponse = {
  success: boolean
  index: string
  indexExists?: boolean
  total: number
  webhook: WebhookInfo
  runs: PublishRun[]
}

const fmt = (n: unknown) => {
  const x = typeof n === 'number' ? n : Number(n)
  if (Number.isFinite(x)) return x.toLocaleString()
  return '-'
}

const fmtDateTime = (value?: string) => {
  if (!value) return '-'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleString()
}

const compactError = (value?: string) => {
  if (!value) return ''
  return value.replace(/\s+/g, ' ').trim()
}

const PublishRunsManager: React.FC = () => {
  const [runs, setRuns] = useState<PublishRun[]>([])
  const [webhook, setWebhook] = useState<WebhookInfo>({ enabled: false, masked_url: '' })
  const [indexExists, setIndexExists] = useState(true)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [actionLoading, setActionLoading] = useState<string | null>(null) // runId:event

  const fetchRuns = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await fetch(`${config.API_URL}/admin/publish-runs?limit=50`)
      if (!response.ok) throw new Error('Failed to fetch publish runs')
      const data: PublishRunsResponse = await response.json()
      setRuns(data.runs || [])
      setWebhook(data.webhook || { enabled: false, masked_url: '' })
      setIndexExists(data.indexExists !== false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchRuns()
  }, [fetchRuns])

  const resend = async (runId: string, event: 'feed_publish.end') => {
    const key = `${runId}:${event}`
    setActionLoading(key)
    setError(null)
    try {
      const resp = await fetch(`${config.API_URL}/admin/publish-runs/${encodeURIComponent(runId)}/resend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ event }),
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) throw new Error(data?.error || 'Resend failed')
      await fetchRuns()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Resend failed')
    } finally {
      setActionLoading(null)
    }
  }

  const confirmAndResend = async (runId: string, event: 'feed_publish.end') => {
    if (actionLoading) return
    const action = 'finished'
    const title = 'Resend FINISHED webhook?'

    const result = await Swal.fire({
      title,
      text: `Type "${action}" to confirm.`,
      input: 'text',
      inputPlaceholder: action,
      showCancelButton: true,
      confirmButtonText: `Resend ${action.toUpperCase()}`,
      cancelButtonText: 'Cancel',
      reverseButtons: true,
      allowOutsideClick: () => !Swal.isLoading(),
      inputValidator: (value) => {
        const v = (value || '').trim().toLowerCase()
        if (v !== action) return `Please type "${action}" to confirm.`
        return undefined
      },
    })

    if (!result.isConfirmed) return
    await resend(runId, event)
  }

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner}></div>
        <span>Loading publish runs...</span>
      </div>
    )
  }

  return (
    <div className={styles.managerContainer}>
      <div className={styles.managerHeader}>
        <div className={styles.headerLeft}>
          <h2>Publish Runs</h2>
          <span className={styles.badge}>
            {runs.length} runs · webhook {webhook.enabled ? 'enabled' : 'disabled'}
          </span>
          {!indexExists && <span className={styles.badge}>index missing (will be created on first run)</span>}
          {webhook.masked_url && <span className={styles.badge}>{webhook.masked_url}</span>}
        </div>
        <div className={styles.headerActions}>
          <button className={styles.refreshButton} onClick={fetchRuns}>
            ↻ Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className={styles.errorBanner}>
          <span>⚠️ {error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Run</th>
              <th>Date</th>
              <th>Expected</th>
              <th>Processed</th>
              <th>Published</th>
              <th>Failed</th>
              <th>Enqueue Done</th>
              <th>Finished</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {runs.length === 0 ? (
              <tr>
                <td colSpan={9} className={styles.emptyRow}>
                  {indexExists
                    ? 'No publish runs found yet'
                    : 'No publish runs yet — the lifecycle index will be created automatically on the first publish round'}
                </td>
              </tr>
            ) : (
              runs.map((r) => {
                let endStatus: 'sent' | 'failed' | '—' = '—'
                if (r.end_webhook_sent_at) endStatus = 'sent'
                else if (r.end_webhook_failed_at || r.end_webhook_fail_reason) endStatus = 'failed'
                // A run that eventually sent is not failed: ignore an error left over
                // from an earlier attempt (older docs may still carry one).
                const failReason = r.end_webhook_sent_at ? '' : r.end_webhook_fail_reason

                return (
                  <tr key={r.id}>
                    <td className={styles.queueName}>
                      <code>{r.id}</code>
                    </td>
                    <td>{r.date_yyyymmdd || '-'}</td>
                    <td>
                      {fmt(r.expected_batches)} batches / {fmt(r.expected_entities)} entities
                    </td>
                    <td>
                      {fmt(r.processed_batches)} batches / {fmt(r.processed_entities)} entities
                    </td>
                    <td>{fmt(r.processed_published)}</td>
                    <td>{fmt(r.processed_failed)}</td>
                    <td>
                      {r.enqueue_complete_at ? fmtDateTime(r.enqueue_complete_at) : r.enqueue_started_at ? 'in_progress' : '-'}
                    </td>
                    <td>
                      <div className={styles.statusStack}>
                        <span className={styles.statusPrimary} title={failReason || ''}>
                          {r.end_webhook_sent_at
                            ? fmtDateTime(r.end_webhook_sent_at)
                            : r.end_webhook_failed_at
                              ? fmtDateTime(r.end_webhook_failed_at)
                              : endStatus}
                          {failReason ? ' (err)' : ''}
                        </span>
                        {r.end_webhook_sent_at && (
                          <div className={styles.statusMeta}>
                            by {r.end_webhook_sent_by === 'admin_resend' ? 'admin (resend)' : 'worker (auto)'}
                          </div>
                        )}
                        {failReason && (
                          <div className={styles.statusErrorDetail} title={failReason}>
                            {compactError(failReason)}
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className={styles.actionButtons}>
                        <button
                          className={styles.actionButton}
                          disabled={!webhook.enabled || actionLoading === `${r.id}:feed_publish.end`}
                          onClick={() => confirmAndResend(r.id, 'feed_publish.end')}
                        >
                          Resend FINISHED
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default PublishRunsManager
